import { memo, useCallback, useEffect, useMemo, useState } from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import { IconButton, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";

import { CurrentPosition, DeviceData } from "../../../../../../API";
import {
  PAN_DEFAULT,
  TILT_DEFAULT,
  ZOOM_DEFAULT,
} from "../../../../../../common/components/inputs/ptz/constants";
import { usePTController } from "../../../../../../common/hooks/usePTController";
import { extractDeviceNameFromId } from "../../../../../../common/utils/getEntityName";
import { selectedStreamObjectVariable } from "../../../../../../common/variables/selectedStreamObject";
import HLSStreamPlayer from "../cameraView/HLSStreamPlayer";
import {
  selectedZoneVariable,
  useFirstZoneVariable,
  useSelectedZoneVariable,
} from "../devices/ptzonelist/zones.variable";
import ChipComponent from "../../../../../../common/components/chip/ChipComponent";
import LocationIcon from "../../../../../../common/components/icons/LocationIcon";
import HashIcon from "../../../../../../common/components/icons/HashIcon";
import DetailsTable, { DetailsTableItem } from "./DetailsTable";
import SectionCard from "./SectionCard";
import LockIcon from "../../../../../../common/components/icons/LockIcon";
import StatusToggle from "../../../../../../common/components/item/StatusToggle";
import ControlTable from "../../../../../../common/components/stream/ControlTable";
import PTZoneListTableContainer from "../devices/ptzonelist/PTZoneListTableContainer";
import mqttPublish from "../../../../../../common/helpers/mqttPublish";
import { useStream } from "../../../../../../common/hooks/useStream";

interface LiveViewDialogProps {
  setOpen: (value: boolean) => void;
  deviceId: string;
  deviceName: string;
  locationName: string;
  currentDevice: string;
  nodeId: string;
  hasPanTilt: boolean;
  hasZoom: boolean;
  deviceData: DeviceData;
  currentPosition?: CurrentPosition | null;
}

interface LiveViewDialogCloseButtonProps {
  onClose: () => void;
}

const LiveViewDialogCloseButton = ({
  onClose,
}: LiveViewDialogCloseButtonProps): JSX.Element => {
  return (
    <IconButton
      disableRipple
      aria-label="close"
      onClick={onClose}
      sx={{
        "&:hover": {
          color: "text.primary",
        },
      }}
    >
      <CloseRounded />
    </IconButton>
  );
};

type PTControllerState = ReturnType<typeof usePTController>;
type PTValidationState = PTControllerState["validationState"];
type PTInputChangeHandler = PTControllerState["handlePanInputChange"];
type PTMoveHandler = PTControllerState["movePtLeft"];
type HandleGoToPt = PTControllerState["handleGoToPt"];

interface LiveViewHeaderProps {
  onClose: () => void;
  onToggleSidebar: () => void;
  isCollapsed: boolean;
  deviceName: string;
}

const LiveViewHeader = memo(
  ({
    deviceName,
    onClose,
    onToggleSidebar,
    isCollapsed,
  }: LiveViewHeaderProps): JSX.Element => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingLeft: isCollapsed ? "0.5em" : "1.5em",
          paddingRight: "0.5em",
        }}
      >
        {!isCollapsed && (
          <Typography sx={{ fontWeight: 500 }} variant="h6" component="h2">
            {deviceName}
          </Typography>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: "0.25em" }}>
          <Tooltip
            title={isCollapsed ? "Show sidebar" : "Hide sidebar"}
            arrow
            placement="left"
          >
            <IconButton
              disableRipple
              aria-label={isCollapsed ? "expand sidebar" : "collapse sidebar"}
              onClick={onToggleSidebar}
              sx={{
                "&:hover": {
                  color: "text.primary",
                },
              }}
            >
              {isCollapsed ? <ChevronLeftRounded /> : <ChevronRightRounded />}
            </IconButton>
          </Tooltip>

          <LiveViewDialogCloseButton onClose={onClose} />
        </Box>
      </Box>
    );
  }
);

LiveViewHeader.displayName = "LiveViewHeader";

interface DeviceDetailsSectionProps {
  items: DetailsTableItem[];
}

export const DeviceDetailsSection = memo(
  ({ items }: DeviceDetailsSectionProps): JSX.Element => {
    return (
      <Box sx={{ marginBottom: "1em" }}>
        <DetailsTable items={items} />
      </Box>
    );
  }
);

DeviceDetailsSection.displayName = "DeviceDetailsSection";

interface ControlModeChipProps {
  manuallyControlled: boolean;
  onModeChange: (checked?: boolean) => void;
}

const ModelControlledChip = memo(
  ({ manuallyControlled }: ControlModeChipProps): JSX.Element => {
    return (
      <ChipComponent
        menuBoxStyles={{
          height: "fit-content",
          backgroundColor: theme =>
            manuallyControlled
              ? theme.palette.otherBackground.main
              : theme.palette.primary.shades?.["8p"],
          border: theme =>
            manuallyControlled
              ? `1px solid ${theme.palette.otherStandardInputLine.main}`
              : `1px solid ${theme.palette.primary.shades?.["30p"]}`,
          cursor: "pointer",
        }}
        textStyles={{
          color: theme =>
            manuallyControlled
              ? theme.palette.otherStandardInputLine.main
              : theme.palette.primary.main,
          fontSize: "12px",
          padding: "1px 0",
        }}
        variant="chip"
        label="Model controlled"
        iconComponent={
          <LockIcon
            color={!manuallyControlled ? "green" : "gray"}
            locked={true}
          />
        }
      />
    );
  }
);

ModelControlledChip.displayName = "ModelControlledChip";

const ManualControlledChip = memo(
  ({ manuallyControlled }: ControlModeChipProps): JSX.Element => {
    return (
      <ChipComponent
        menuBoxStyles={{
          backgroundColor: theme =>
            manuallyControlled
              ? theme.palette.warning.shades?.["4p"]
              : theme.palette.otherBackground.main,
          height: "fit-content",
          cursor: "pointer",
          border: theme =>
            manuallyControlled
              ? `1px solid ${theme.palette.warning.shades?.["12p"]}`
              : `1px solid ${theme.palette.otherStandardInputLine.main}`,
        }}
        textStyles={{
          color: theme =>
            manuallyControlled
              ? theme.palette.warning.main
              : theme.palette.otherStandardInputLine.main,
          fontSize: "12px",
          padding: "1px 0",
        }}
        variant="chip"
        label="Manually controlled"
        iconComponent={
          <LockIcon
            color={manuallyControlled ? "orange" : "gray"}
            locked={false}
          />
        }
      />
    );
  }
);

ManualControlledChip.displayName = "ManualControlledChip";

interface PTControlModeRowProps {
  manuallyControlled: boolean;
  onModeChange: (checked?: boolean) => void;
}

const PTControlModeRow = memo(
  ({
    manuallyControlled,
    onModeChange,
  }: PTControlModeRowProps): JSX.Element => {
    return (
      <Box
        sx={{
          padding: "0.5em 1em",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <ModelControlledChip
            manuallyControlled={manuallyControlled}
            onModeChange={onModeChange}
          />
        </Box>

        <Box>
          <Tooltip
            title={`When disabled, the camera is managed by the model.\nThe model pauses while the PT controller is active.`}
            arrow
            placement="top"
          >
            <Box>
              <StatusToggle
                checked={manuallyControlled}
                handleChange={() => onModeChange()}
                label=""
              />
            </Box>
          </Tooltip>
        </Box>

        <Box>
          <ManualControlledChip
            manuallyControlled={manuallyControlled}
            onModeChange={onModeChange}
          />
        </Box>
      </Box>
    );
  }
);

PTControlModeRow.displayName = "PTControlModeRow";

interface PTControllerSectionProps {
  manuallyControlled: boolean;
  onModeChange: (checked?: boolean) => void;
  pan: string;
  tilt: string;
  zoom: string;
  disabled: boolean;
  validationState: PTValidationState;
  handleGoToPt: HandleGoToPt;
  handlePanInputChange: PTInputChangeHandler;
  handleTiltInputChange: PTInputChangeHandler;
  handleZoomInputChange: PTInputChangeHandler;
  movePtLeft: PTMoveHandler;
  movePtRight: PTMoveHandler;
  movePtUp: PTMoveHandler;
  movePtDown: PTMoveHandler;
  hasZoom: boolean;
  icon: JSX.Element;
}

const PTControllerSection = memo(
  ({
    manuallyControlled,
    onModeChange,
    pan,
    tilt,
    zoom,
    disabled,
    validationState,
    handleGoToPt,
    handlePanInputChange,
    handleTiltInputChange,
    handleZoomInputChange,
    movePtLeft,
    movePtRight,
    movePtUp,
    movePtDown,
    hasZoom,
    icon,
  }: PTControllerSectionProps): JSX.Element => {
    return (
      <SectionCard title="PT Controller" icon={icon}>
        <PTControlModeRow
          manuallyControlled={manuallyControlled}
          onModeChange={onModeChange}
        />

        {manuallyControlled && (
          <Box
            sx={{
              padding: "0.5em 1em",
              borderTop: theme =>
                `1px solid ${theme.palette.otherStandardInputLine.main}`,
            }}
          >
            <ControlTable
              tableStyles={{ width: "100%" }}
              pan={pan}
              manuallyControlled={true}
              tilt={tilt}
              zoom={zoom}
              ptDisabled={false}
              validation={validationState}
              showValidationAlert
              handleGoToPt={handleGoToPt}
              handlePanInputChange={handlePanInputChange}
              handleTiltInputChange={handleTiltInputChange}
              handleZoomInputChange={handleZoomInputChange}
              movePtLeft={movePtLeft}
              movePtRight={movePtRight}
              movePtUp={movePtUp}
              movePtDown={movePtDown}
              hasZoom={hasZoom}
            />
          </Box>
        )}
      </SectionCard>
    );
  }
);

PTControllerSection.displayName = "PTControllerSection";

interface ZonesListSectionProps {
  currentDevice: string;
  nodeId: string;
  hasZoom: boolean;
  disabled: boolean;
  handleGoToPt: HandleGoToPt;
}

const ZonesListSection = memo(
  ({
    currentDevice,
    nodeId,
    hasZoom,
    disabled,
    handleGoToPt,
  }: ZonesListSectionProps): JSX.Element => {
    return (
      <Box
        sx={{
          margin: "2em 0 0 0",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          maxHeight: "100%",
          overflow: "hidden",
        }}
      >
        <SectionCard
          title="Zones List"
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "fit-content",
            maxHeight: "100%",
            overflow: "hidden",
            alignSelf: "stretch",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <PTZoneListTableContainer
              currentDeviceId={currentDevice}
              nodeId={nodeId}
              hasZoom={hasZoom}
              disableRowClick={disabled}
              handleGoToPt={handleGoToPt}
            />
          </Box>
        </SectionCard>
      </Box>
    );
  }
);

ZonesListSection.displayName = "ZonesListSection";

interface LiveViewSidebarProps {
  deviceName: string;
  onClose: () => void;
  onToggleSidebar: () => void;
  isCollapsed: boolean;
  deviceDetailsItems: DetailsTableItem[];
  manuallyControlled: boolean;
  onModeChange: (checked?: boolean) => void;
  hasPanTilt: boolean;
  pan: string;
  tilt: string;
  zoom: string;
  disabled: boolean;
  validationState: PTValidationState;
  handleGoToPt: HandleGoToPt;
  handlePanInputChange: PTInputChangeHandler;
  handleTiltInputChange: PTInputChangeHandler;
  handleZoomInputChange: PTInputChangeHandler;
  movePtLeft: PTMoveHandler;
  movePtRight: PTMoveHandler;
  movePtUp: PTMoveHandler;
  movePtDown: PTMoveHandler;
  hasZoom: boolean;
  currentDevice: string;
  nodeId: string;
}

const LiveViewSidebar = memo(
  ({
    deviceName,
    onClose,
    onToggleSidebar,
    isCollapsed,
    deviceDetailsItems,
    manuallyControlled,
    onModeChange,
    hasPanTilt,
    pan,
    tilt,
    zoom,
    disabled,
    validationState,
    handleGoToPt,
    handlePanInputChange,
    handleTiltInputChange,
    handleZoomInputChange,
    movePtLeft,
    movePtRight,
    movePtUp,
    movePtDown,
    hasZoom,
    currentDevice,
    nodeId,
  }: LiveViewSidebarProps): JSX.Element => {
    const ptControllerIcon = useMemo(
      () => (
        <Tooltip
          title={`When disabled, the camera is managed by the model.\nThe model pauses while the PT controller is active.`}
          arrow
          placement="left"
        >
          <ErrorOutlineIcon
            sx={theme => ({
              color: theme.palette.otherTextTertiary.main,
            })}
          />
        </Tooltip>
      ),
      []
    );

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: isCollapsed ? "100px" : "450px",
          minWidth: isCollapsed ? "45px" : "450px",
          transition: "width 0.2s ease",
        }}
      >
        <LiveViewHeader
          onClose={onClose}
          onToggleSidebar={onToggleSidebar}
          isCollapsed={isCollapsed}
          deviceName={deviceName}
        />

        {!isCollapsed && (
          <Box
            sx={{
              padding: "0.5em 1.5em",
              paddingBottom: 0,
              minWidth: "450px",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
              }}
            >
              <DeviceDetailsSection items={deviceDetailsItems} />

              {hasPanTilt && (
                <>
                  <PTControllerSection
                    manuallyControlled={manuallyControlled}
                    onModeChange={onModeChange}
                    pan={pan}
                    tilt={tilt}
                    zoom={zoom}
                    disabled={disabled}
                    validationState={validationState}
                    handleGoToPt={handleGoToPt}
                    handlePanInputChange={handlePanInputChange}
                    handleTiltInputChange={handleTiltInputChange}
                    handleZoomInputChange={handleZoomInputChange}
                    movePtLeft={movePtLeft}
                    movePtRight={movePtRight}
                    movePtUp={movePtUp}
                    movePtDown={movePtDown}
                    hasZoom={hasZoom}
                    icon={ptControllerIcon}
                  />

                  <ZonesListSection
                    currentDevice={currentDevice}
                    nodeId={nodeId}
                    hasZoom={hasZoom}
                    disabled={!manuallyControlled}
                    handleGoToPt={handleGoToPt}
                  />
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  }
);

LiveViewSidebar.displayName = "LiveViewSidebar";

const LiveViewDialog = ({
  setOpen,
  deviceId,
  deviceName,
  locationName,
  currentDevice,
  nodeId,
  hasPanTilt,
  hasZoom,
  deviceData,
  currentPosition,
}: LiveViewDialogProps) => {
  const selectedZone = useSelectedZoneVariable();

  const firstZoneVariable = useFirstZoneVariable();

  const { kinesisStreamMessage, panTiltMessage } = mqttPublish();

  const {
    pan,
    tilt,
    zoom,
    disabled,
    movePtUp,
    movePtDown,
    movePtLeft,
    movePtRight,
    handleGoToPt,
    handlePanInputChange,
    handleTiltInputChange,
    handleZoomInputChange,
    validationState,
  } = usePTController({
    deviceId,
    deviceName,
    nodeId,
    deviceData,
    currentPosition,
    zoneName: selectedZone.zoneName,
  });

  const { getStreamName } = useStream();

  const [manuallyControlled, setManuallyControlled] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const { extractedDeviceName } = extractDeviceNameFromId(deviceId);

  const deviceIdClean = useMemo(() => {
    return deviceId.replace(/^DE#/, "");
  }, [deviceId]);

  useEffect(() => {
    const streamName = getStreamName(nodeId, deviceId);

    kinesisStreamMessage({
      action: "START",
      deviceName: extractedDeviceName,
      deviceId,
      nodeId,
    }).then((): void => {
      selectedStreamObjectVariable({
        streamName,
        extractedDeviceName,
        nodeId,
      });
    });

    return () => {
      kinesisStreamMessage({
        action: "END",
        deviceName: extractedDeviceName,
        deviceId,
        nodeId,
      });
    };
  }, []);

  const handleClose = async () => {
    const firstZonePan = firstZoneVariable?.pan ?? PAN_DEFAULT;
    const firstZoneTilt = firstZoneVariable?.tilt ?? TILT_DEFAULT;
    const firstZoneZoom = firstZoneVariable?.zoom ?? ZOOM_DEFAULT;
    const firstZoneName = firstZoneVariable?.zoneName ?? "";

    setOpen(false);

    await kinesisStreamMessage({
      action: "END",
      deviceName: extractedDeviceName,
      deviceId,
      nodeId,
    });

    selectedStreamObjectVariable(null);

    if (manuallyControlled) {
      handleGoToPt(firstZonePan, firstZoneTilt, firstZoneZoom, firstZoneName);

      await panTiltMessage({
        action: "STOP",
        deviceName: extractedDeviceName,
        nodeId,
      });

      selectedZoneVariable({
        zoneName: firstZoneName,
        pan: firstZonePan,
        tilt: firstZoneTilt,
        zoom: firstZoneZoom,
      });
    }
  };

  const deviceDetailsItems = useMemo<DetailsTableItem[]>(
    () => [
      {
        label: "Location",
        content: (
          <ChipComponent
            variant="chip"
            label={locationName}
            iconComponent={<LocationIcon />}
            textStyles={{ fontWeight: 500 }}
          />
        ),
      },
      {
        label: "Device ID",
        content: (
          <ChipComponent
            variant="chip"
            label={deviceIdClean}
            iconComponent={<HashIcon />}
            textStyles={{ fontWeight: 500 }}
          />
        ),
      },
    ],
    [deviceName, locationName]
  );

  const manuallyControlledHandler = useCallback(
    async (checked?: boolean): Promise<void> => {
      if (typeof checked === "boolean") {
        setManuallyControlled(checked);

        await panTiltMessage({
          action: checked ? "START" : "STOP",
          deviceName: extractedDeviceName,
          nodeId,
        });

        return;
      }

      setManuallyControlled(state => !state);

      await panTiltMessage({
        action: !manuallyControlled ? "START" : "STOP",
        deviceName: extractedDeviceName,
        nodeId,
      });
    },
    [setManuallyControlled, manuallyControlled, extractedDeviceName, nodeId]
  );

  const toggleSidebar = useCallback((): void => {
    setIsSidebarOpen(state => !state);
  }, []);

  return (
    <Dialog
      PaperProps={{
        sx: {
          borderRadius: "0px",
          maxWidth: "100%",
          maxHeight: "100%",
          width: "100%",
          height: "100%",
          margin: 0,
        },
      }}
      open={true}
      fullWidth
    >
      <DialogContent
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          overflowY: "hidden",
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: "100%",
            flex: 1,
            display: "flex",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <HLSStreamPlayer />
        </Box>

        <LiveViewSidebar
          deviceName={deviceName}
          onClose={handleClose}
          onToggleSidebar={toggleSidebar}
          isCollapsed={!isSidebarOpen}
          deviceDetailsItems={deviceDetailsItems}
          manuallyControlled={manuallyControlled}
          onModeChange={manuallyControlledHandler}
          hasPanTilt={hasPanTilt}
          pan={pan}
          tilt={tilt}
          zoom={zoom}
          disabled={disabled}
          validationState={validationState}
          handleGoToPt={handleGoToPt}
          handlePanInputChange={handlePanInputChange}
          handleTiltInputChange={handleTiltInputChange}
          handleZoomInputChange={handleZoomInputChange}
          movePtLeft={movePtLeft}
          movePtRight={movePtRight}
          movePtUp={movePtUp}
          movePtDown={movePtDown}
          hasZoom={hasZoom}
          currentDevice={currentDevice}
          nodeId={nodeId}
        />
      </DialogContent>
    </Dialog>
  );
};

export default LiveViewDialog;
